import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Drawer,
  FailedCallout,
  FieldHint,
  FieldLabel,
  ReasonField,
  TextField,
  TextInput,
  isApiError,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { profileOptionsQuery, sourceCommands, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";
import { PersonCard, PersonOptions, TierField, personOptions, type PersonEntry, type Tier } from "./SourceFormParts";

function AddSourceFooter({ pending, failed, onClose }: { pending: boolean; failed: boolean; onClose: () => void }) {
  return (
    <span className="flex items-center justify-end gap-2">
      <Button size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button size="sm" variant="primary" type="submit" form="add-source-form" aria-busy={pending || undefined}>
        {failed ? "Try again" : "Add source"}
      </Button>
    </span>
  );
}

export function AddSourcePanel({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const toast = useToast();
  const profiles = useQuery(profileOptionsQuery);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [people, setPeople] = useState<PersonEntry[]>([]);
  const [find, setFind] = useState("");
  const [tier, setTier] = useState<Tier | "">("");
  const required = people.some((p) => p.inPlan && p.required);
  const defaultTier: Tier = required ? "priority" : "standard";
  const effectiveTier: Tier = tier === "" ? defaultTier : tier;
  const mutation = useCommandMutation({
    run: (csrf, reason: string) =>
      sourceCommands.add(
        {
          url: url.trim(),
          ...(title.trim() ? { title: title.trim() } : {}),
          people: people.map((p) => ({ profile_id: p.profile_id, relationship: p.relationship ?? "own" })),
          plans: people.filter((p) => p.inPlan).map((p) => ({ profile_id: p.profile_id, required: p.required })),
          cadence_tier: effectiveTier,
          known_breaks: [],
          reason,
        },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: (result) => {
      toast.show(
        people.some((p) => p.inPlan) ? "Source added · plans show Changes pending" : "Source added · Not in any plan",
      );
      onAdded(result.id);
    },
  });
  const server = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const r = useReason(server["reason"]);
  const urlError = server["url"] ?? (r.submitted && url.trim().length === 0 ? "Paste the address of the source" : null);
  const options = useMemo(() => personOptions(profiles.data?.profiles, find, people), [profiles.data, find, people]);
  const nameOf = (id: string) => profiles.data?.profiles.find((p) => p.id === id)?.name ?? id;
  const update = (id: string, patch: Partial<PersonEntry>) => {
    setPeople(people.map((p) => (p.profile_id === id ? { ...p, ...patch } : p)));
  };
  return (
    <Drawer
      title="Add source"
      subtitle="A channel, account, programme, feed, official record or website"
      onClose={onClose}
      footer={<AddSourceFooter pending={mutation.isPending} failed={mutation.isError} onClose={onClose} />}
    >
      <form
        id="add-source-form"
        noValidate
        className="text-ui [&>div:first-child]:mt-0"
        onSubmit={(e) => {
          e.preventDefault();
          if (!r.submit() || url.trim().length === 0) return;
          mutation.mutate(r.trimmed);
        }}
      >
        <TextField
          label="Address"
          required
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder="https://"
          value={url}
          error={urlError}
          hint="The platform is detected from the address; platforms we don't collect are recorded as known accounts"
          onChange={(e) => {
            setUrl(e.target.value);
          }}
        />
        <TextField
          label="Name"
          optional="optional"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
        <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
          <legend className="contents">
            <FieldLabel optional="optional">People</FieldLabel>
          </legend>
          {people.map((p) => (
            <PersonCard
              key={p.profile_id}
              entry={p}
              name={nameOf(p.profile_id)}
              onUpdate={(patch) => {
                update(p.profile_id, patch);
              }}
              onRemove={() => {
                setPeople(people.filter((x) => x.profile_id !== p.profile_id));
              }}
            />
          ))}
          <label className="sr-only" htmlFor="add-source-find">
            Find a person
          </label>
          <TextInput
            id="add-source-find"
            type="search"
            placeholder="Add a person"
            value={find}
            onChange={(e) => {
              setFind(e.target.value);
            }}
          />
          <PersonOptions
            options={options}
            onPick={(id) => {
              setPeople([...people, { profile_id: id, relationship: "own", inPlan: true, required: false }]);
              setFind("");
            }}
          />
          <FieldHint>
            {people.some((p) => p.inPlan)
              ? "Their plans show Changes pending until the scheduler applies them; then the first scan runs."
              : "Without a plan the source is saved as Not in any plan and is not scanned. A source without people can be monitored with Monitor this source…"}
          </FieldHint>
        </fieldset>
        <TierField id="add-source-tier" value={effectiveTier} onChange={setTier} />
        <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
        <FailedCallout error={mutation.error} fieldsShown />
      </form>
    </Drawer>
  );
}
